import { parseISO, isToday } from "date-fns";
import { Transaction } from "@/context/FinanceContext";

export interface Account {
  id: string;
  name: string;
  initialBalance?: number;
}

export interface PaymentSummary {
  total: number;
  count: number;
  transactions: Transaction[];
}

export interface FinancialBalance {
  currentBalance: number;
  todayMovements: number;
  projectedTodayBalance: number;
  futureBalance: number;
  totalReceivables: number;
  totalPayables: number;
  totalBalance: number;
}

/**
 * Formats currency value without currency symbol
 * @param value - The numeric value to format
 * @param showSign - Whether to show +/- sign
 * @returns Formatted currency string
 */
export function formatCurrency(value: number, showSign = true): string {
  const formatted = new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Math.abs(value));
  
  if (!showSign) return formatted;
  const sign = value < 0 ? '- ' : '';
  return `${sign}${formatted}`;
}

/**
 * Formats currency value without decimals (compact version)
 * @param value - The numeric value to format
 * @param showSign - Whether to show +/- sign
 * @returns Formatted currency string without decimals
 */
export function formatCurrencyCompact(value: number, showSign = true): string {
  const formatted = new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Math.abs(value));
  
  if (!showSign) return formatted;
  const sign = value < 0 ? '- ' : '';
  return `${sign}${formatted}`;
}

/**
 * Calculates the current balance across all accounts
 * @param accounts - Array of accounts with initial balances
 * @param transactions - Array of settled transactions
 * @returns Total current balance
 */
export function calculateCurrentBalance(
  accounts: Account[],
  transactions: Transaction[]
): number {
  return accounts.reduce((totalBalance, account) => {
    let balance = account.initialBalance || 0;
    const accountTransactions = transactions.filter(
      t => t.accountId === account.id && t.settled
    );
    
    accountTransactions.forEach(t => {
      if (t.type === "receita") {
        balance += t.value;
      } else {
        balance -= t.value;
      }
    });
    
    return totalBalance + balance;
  }, 0);
}

/**
 * Filters and summarizes overdue transactions
 * @param transactions - Array of pending transactions
 * @param type - Transaction type ('receita' or 'despesa')
 * @returns Summary of overdue transactions
 */
export function getOverdueSummary(
  transactions: Transaction[],
  type: 'receita' | 'despesa'
): PaymentSummary {
  const today = new Date();
  const overdueTransactions = transactions.filter(t => {
    const dueDate = t.dueDate ? parseISO(t.dueDate) : null;
    return t.type === type && 
           !t.settled && 
           dueDate && 
           dueDate < today && 
           !isToday(dueDate);
  });

  return {
    total: overdueTransactions.reduce((sum, t) => sum + t.value, 0),
    count: overdueTransactions.length,
    transactions: overdueTransactions
  };
}

/**
 * Filters and summarizes transactions due today
 * @param transactions - Array of pending transactions
 * @param type - Transaction type ('receita' or 'despesa')
 * @returns Summary of today's transactions
 */
export function getTodaySummary(
  transactions: Transaction[],
  type: 'receita' | 'despesa'
): PaymentSummary {
  const todayTransactions = transactions.filter(t => {
    const dueDate = t.dueDate ? parseISO(t.dueDate) : null;
    return t.type === type && !t.settled && dueDate && isToday(dueDate);
  });

  return {
    total: todayTransactions.reduce((sum, t) => sum + t.value, 0),
    count: todayTransactions.length,
    transactions: todayTransactions
  };
}

/**
 * Filters and summarizes future transactions (with due date > today)
 * @param transactions - Array of pending transactions
 * @param type - Transaction type ('receita' or 'despesa')
 * @returns Summary of future transactions
 */
export function getFutureSummary(
  transactions: Transaction[],
  type: 'receita' | 'despesa'
): PaymentSummary {
  const today = new Date();
  const futureTransactions = transactions.filter(t => {
    const dueDate = t.dueDate ? parseISO(t.dueDate) : null;
    return t.type === type && !t.settled && dueDate && dueDate > today;
  });

  return {
    total: futureTransactions.reduce((sum, t) => sum + t.value, 0),
    count: futureTransactions.length,
    transactions: futureTransactions
  };
}

/**
 * Filters and summarizes transactions without a due date (open/pending)
 * @param transactions - Array of pending transactions
 * @param type - Transaction type ('receita' or 'despesa')
 * @returns Summary of transactions without due date
 */
export function getNoDueDateSummary(
  transactions: Transaction[],
  type: 'receita' | 'despesa'
): PaymentSummary {
  const noDueDateTransactions = transactions.filter(t => {
    return t.type === type && !t.settled && !t.dueDate;
  });

  return {
    total: noDueDateTransactions.reduce((sum, t) => sum + t.value, 0),
    count: noDueDateTransactions.length,
    transactions: noDueDateTransactions
  };
}

/**
 * Filters and summarizes "Em Aberto" transactions (future + no due date)
 * These are transactions that are not overdue and not due today
 * @param transactions - Array of pending transactions
 * @param type - Transaction type ('receita' or 'despesa')
 * @returns Summary of open transactions
 */
export function getOpenSummary(
  transactions: Transaction[],
  type: 'receita' | 'despesa'
): PaymentSummary {
  const today = new Date();
  const openTransactions = transactions.filter(t => {
    if (t.type !== type || t.settled) return false;
    
    // No due date = open
    if (!t.dueDate) return true;
    
    // Future due date = open
    const dueDate = parseISO(t.dueDate);
    return dueDate > today;
  });

  return {
    total: openTransactions.reduce((sum, t) => sum + t.value, 0),
    count: openTransactions.length,
    transactions: openTransactions
  };
}

/**
 * Calculates comprehensive financial balance summary
 * @param accounts - Array of accounts
 * @param transactions - Array of all transactions
 * @returns Complete financial balance data
 */
export function calculateFinancialBalance(
  accounts: Account[],
  transactions: Transaction[]
): FinancialBalance {
  const currentBalance = calculateCurrentBalance(accounts, transactions);
  const pendingTransactions = transactions.filter(t => !t.settled);

  // Receivables and payables by period
  const overdueReceivables = getOverdueSummary(pendingTransactions, 'receita');
  const overduePayables = getOverdueSummary(pendingTransactions, 'despesa');
  const todayReceivables = getTodaySummary(pendingTransactions, 'receita');
  const todayPayables = getTodaySummary(pendingTransactions, 'despesa');
  const futureReceivables = getFutureSummary(pendingTransactions, 'receita');
  const futurePayables = getFutureSummary(pendingTransactions, 'despesa');

  // Totals
  const totalReceivables = overdueReceivables.total + todayReceivables.total + futureReceivables.total;
  const totalPayables = overduePayables.total + todayPayables.total + futurePayables.total;
  const totalBalance = totalReceivables - totalPayables;

  // Today's movements
  const todayMovements = todayReceivables.total - todayPayables.total;
  const projectedTodayBalance = currentBalance + todayMovements;
  
  // Future balance
  const futureBalance = futureReceivables.total - futurePayables.total;

  return {
    currentBalance,
    todayMovements,
    projectedTodayBalance,
    futureBalance,
    totalReceivables,
    totalPayables,
    totalBalance
  };
}

/**
 * Gets all pending receivables and payables
 * @param transactions - Array of all transactions
 * @returns Object with receivables and payables arrays
 */
export function getPendingTransactions(transactions: Transaction[]): {
  receivables: Transaction[];
  payables: Transaction[];
} {
  const pending = transactions.filter(t => !t.settled);
  return {
    receivables: pending.filter(t => t.type === "receita"),
    payables: pending.filter(t => t.type === "despesa")
  };
}
